import AppUtils from "../AppUtils";
import { getDivTemplate, getSpanTemplate, getLiTemplate } from "../../PdfTemplates/prescriptionBody";
import { isEmpty as _isEmpty, map as _map, get as _get } from "lodash";
import { TreatmentPlanFollowUpObj, TreatmentPlanReferralObj, TreatmentPlanLabTestObj, TreatmentPlanAdviceObj } from "../../models/ITreatmentPlanTemplateResponse";
import { IPdfTemplates } from "../../models/PdfTemplatesModels";
import { PrescriptionPdfSection, AppConstants, vitalHeaderMap } from "../AppConstants";
import { ITreatmentPlanPdf, TreatmentPlanMedicinesIndexObj } from "./TreatmentPlanPdfUtil";
import { IConsultationPdfData } from "../../components/templates/CommonPrescriptionPdf";
import { IDiagnosis } from "../../models/patients/IEHRSavePatientMedicalProfile";
import { ISymptom } from "@curefit/care-common";
import { IPatientSystemiscExamination } from "@curefit/ehr-common";
import {
    adviceTemplate, allergiesContTemplate,
    carefitHeader,
    chiefComplaintsItem,
    chiefComplaintsTemplate, entExaminationsContTemplate, entExamItemTemplate,
    followupItem,
    followupTemplate,
    footerNewTemplate,
    footerTemplate,
    generalExaminationContTemplate,
    prescriptionCardHeader,
    prescriptionDiagnosisItem,
    prescriptionDiagnosisTemplate,
    prescriptionLabTestItem,
    prescriptionLabTestsTemplate,
    prescriptionMedicineItem,
    prescriptionMedicinesTemplate,
    publicNotesTemplate,
    referralItem,
    referralTemplate,
    signatureContTemplate, systemicExaminationsContTemplate, systemicExamItemTemplate, bodyPartsContTemplate, bodyPartsItemTemplate
} from "./temporaryNewTemplates";

export interface IDoctorPrescriptionPdf extends Omit<ITreatmentPlanPdf, "prescriptionDiagnosis"> {
    prescriptionDiagnosis: Array<IDiagnosis>
    chiefComplaints: {
        chiefSymptoms: any
        presentIllness: any
    }
    publicNotes: any
    vitals: any
    generalExaminations: any
    systemicExaminations: any
    entExamination: any
    allergies: any,
    prescriptionBodyPartsCateredTo: any
}

export const doctorPrescriptionFormatState = (consultationData: IConsultationPdfData): IDoctorPrescriptionPdf => {
    let newPrescription = {} as IDoctorPrescriptionPdf
    if (!_isEmpty(consultationData.consultation)) {
        if (!_isEmpty(consultationData.consultation.prescription)) {
            let prescriptionItem = consultationData.consultation.prescription;
            let symptoms: any = [];
            if (prescriptionItem.chiefComplaints) {
                symptoms = prescriptionItem.chiefComplaints.patientComplaints || [];
            }
            let patientSymptomSet: any = {};
            if (!_isEmpty(symptoms)) {
                patientSymptomSet.chiefSymptoms = symptoms;
            }
            if (prescriptionItem.chiefComplaints && prescriptionItem.chiefComplaints.doctorPublicNotes && !_isEmpty(prescriptionItem.chiefComplaints.doctorPublicNotes)) {
                newPrescription.publicNotes = [prescriptionItem.chiefComplaints.doctorPublicNotes.publicNotes]
            }
            if (prescriptionItem.chiefComplaints && prescriptionItem.chiefComplaints.presentIllness) {
                patientSymptomSet.presentIllness = prescriptionItem.chiefComplaints.presentIllness
            }
            newPrescription.chiefComplaints = patientSymptomSet;
            const { examinations, systemicExaminations, entExaminations, allergies } = consultationData;
            newPrescription.followup = prescriptionItem.prescriptionFollowUp && prescriptionItem.prescriptionFollowUp.followup ? [prescriptionItem.prescriptionFollowUp.followup] : null;
            newPrescription.referral = prescriptionItem.prescriptionReferral && prescriptionItem.prescriptionReferral.referral ? prescriptionItem.prescriptionReferral.referral : null;
            if (!_isEmpty(examinations)) {
                newPrescription.generalExaminations = examinations;
            }
            if (!_isEmpty(systemicExaminations)) {
                let sysExaminations: any = {};
                const allExams = _map(systemicExaminations, item => item).filter(item => typeof item === "object");
                let normal = allExams.filter(exm => exm.isNormal);
                let sysExm = allExams.filter(exm => exm.comment);
                if (normal && normal.length > 0) {
                    sysExaminations.normal = normal;
                }
                if (sysExm && sysExm.length > 0) {
                    sysExaminations.sysExm = sysExm;
                }
                newPrescription.systemicExaminations = sysExaminations;
            }
            if (!_isEmpty(entExaminations)) {
                let modifiedEntExaminations: any = {};
                const allExams = _map(entExaminations, item => item).filter(item => typeof item === "object");
                let normal = allExams.filter(exm => exm.isNormal);
                let entExm = allExams.filter(exm => exm.comment);
                if (normal && normal.length > 0) {
                    modifiedEntExaminations.normal = normal;
                }
                if (entExm && entExm.length > 0) {
                    modifiedEntExaminations.entExm = entExm;
                }
                newPrescription.entExamination = modifiedEntExaminations;
            }

            if (!_isEmpty(allergies) && !_isEmpty(allergies.allergenList)) {
                newPrescription.allergies = allergies.allergenList;
            }
            if (!_isEmpty(allergies) && allergies.noAllergy && allergies.noAllergy.noDrugAllergy) {
                (newPrescription.allergies)
                    ? newPrescription.allergies = [...newPrescription.allergies, { allergen: { displayValue: "No Known Drug Allergy" } }]
                    : newPrescription.allergies = [{ allergen: { displayValue: "No Known Drug Allergy" } }];
            }

            if (prescriptionItem && prescriptionItem.prescriptionDiagnosis && prescriptionItem.prescriptionDiagnosis.diagnosticsList) {
                newPrescription.prescriptionDiagnosis = prescriptionItem.prescriptionDiagnosis.diagnosticsList
            }
            if (prescriptionItem.prescriptionLabTest && prescriptionItem.prescriptionLabTest.labTestList) {
                newPrescription.prescriptionLabTest = prescriptionItem.prescriptionLabTest.labTestList
            }
            if (prescriptionItem.prescriptionMedicines && prescriptionItem.prescriptionMedicines.prescriptionMedicationList) {
                newPrescription.prescriptionMedicines = prescriptionItem.prescriptionMedicines.prescriptionMedicationList.map(
                    (meds: any, index: number) => {
                        return { ...meds, arrIndex: index }
                    })
            }
            if (prescriptionItem.prescriptionAdvice && prescriptionItem.prescriptionAdvice.advice) {
                newPrescription.prescriptionAdvice = prescriptionItem.prescriptionAdvice.advice
            }
            if (prescriptionItem.prescriptionBodyPartsCateredTo && prescriptionItem.prescriptionBodyPartsCateredTo.bodyPartsWorkedOn) {
                newPrescription.prescriptionBodyPartsCateredTo = prescriptionItem.prescriptionBodyPartsCateredTo.bodyPartsWorkedOn;
            }
        }
    }
    return newPrescription;
}

export const doctorPrescriptionModifyTemplate = (
    pdfTemplates: IPdfTemplates,
    props: IConsultationPdfData,
    prescription: Readonly<any>,
    pageIndex?: number,
    templateList?: any[]
): string => {

    const getDoctorDetailsTemplate = () => {
        const doctorProfileObj = props.appointmentSchedule.doctor;
        if (pdfTemplates.templateNameToDataMap && pdfTemplates.templateNameToDataMap["doctorDetails"] && doctorProfileObj) {
            let doctorPrefix = !_isEmpty(doctorProfileObj.prefix) ? doctorProfileObj.prefix + " " : ""
            return pdfTemplates.templateNameToDataMap["doctorDetails"]
                .replace("$doctorName", doctorPrefix + !_isEmpty(doctorProfileObj.name) ? doctorProfileObj.name : "")
                .replace("$doctorQualifications", !_isEmpty(doctorProfileObj.qualification) ? doctorProfileObj.qualification : "")
                .replace("$doctorKmc", !_isEmpty(doctorProfileObj.registrationNumber) ? doctorProfileObj.registrationNumber : "")
                .replace("$centerName", props.appointmentSchedule.center.name);
        }
        return "";
    }

    const getPrescriptionCardHeaderTemplate = () => {
        if (pdfTemplates.templateNameToDataMap["prescriptionCardHeader"]) {
            const date = AppUtils.getDayEpochInfo(props.appointmentSchedule.startTime);
            const patientGender = props.patient.gender || "";
            const patientAge = props.patient.age && props.patient.age > 0 ? props.patient.age.toString() : "";
            let reasonForVisit = ""
            if (!_isEmpty(props.consultation.prescription.reasonForVisit) && !_isEmpty(props.consultation.prescription.reasonForVisit.reasonForVisitOptions)) {
                reasonForVisit = props.consultation.prescription.reasonForVisit.reasonForVisitOptions.reason
                if (reasonForVisit === "HCU") {
                    reasonForVisit = "Health Checkup Report Review"
                }
                else if (reasonForVisit === "Follow Up") {
                    reasonForVisit += " Consultation"
                }
            }
            reasonForVisit = [...reasonForVisit].map((ch, i) => i === 0 ? ch.toUpperCase() : ch).join(""); // tmp hack
            const patientDetails = patientGender.length > 0 || patientAge.length > 0
                ? ` (${patientGender}${patientGender.length && patientAge.length ? ", " : ""}${patientAge})`
                : props.patient.name;
            return prescriptionCardHeader
                .replace("$patientName", props.patient.name)
                .replace("$patientDetails", patientDetails)
                .replace("$date", `${date.dateText} ${date.month}, ${date.year}`)
                .replace("$uhid", props.patient.uhid)
                .replace("$reasonForVisit", reasonForVisit)

        }
        return "";
    }

    const getSignatureTemplate = () => {
        // if (pdfTemplates.templateNameToDataMap["signatureContainer"]) {
        //     return pdfTemplates.templateNameToDataMap["signatureContainer"]
        return signatureContTemplate
            .replace("$signatureUrl", AppUtils.isDefined(props.signatureImagePreSignedUrl) ? props.signatureImagePreSignedUrl.url : "")
            .replace("$injectAdditionaInfoText", AppUtils.isTeleConsultationAppointment(props.appointmentSchedule) ? "* This prescription is generated on a teleconsultation" : "")
        // }
        // return "";
    }

    const getFooterTemplate = () => {
        // if (pdfTemplates.templateNameToDataMap["footer"]) {
        //     return pdfTemplates.templateNameToDataMap["footer"]
        const totalPages = (templateList || []).length
        return footerNewTemplate
            .replace("$centerAddress", props.appointmentSchedule.center.address)
            .replace("$pageDetail", `Page ${pageIndex && pageIndex > 0
                ? pageIndex + 1
                : 1
                } of ${totalPages || 1}`)
        // }
        // return "";
    }

    const getPrescriptionPdfTemplate = (pageIndex: number, totalPages?: number) => {
        let prescriptionTemp = "";
        // const headerTemplate = pdfTemplates.templateNameToDataMap["carefitHeader"];
        const doctorProfileObj = props.appointmentSchedule.doctor;
        let doctorPrefix = !_isEmpty(doctorProfileObj.prefix) ? doctorProfileObj.prefix + " " : ""
        const headerTemplate = carefitHeader.replace("$doctorName", doctorPrefix + !_isEmpty(doctorProfileObj.name) ? doctorProfileObj.name : "")
            .replace("$doctorQualifications", !_isEmpty(doctorProfileObj.qualification) ? doctorProfileObj.qualification : "")
            .replace("$doctorKmc", !_isEmpty(doctorProfileObj.registrationNumber) ? doctorProfileObj.registrationNumber : "")
        let prescriptionCardHeaderTemplate = getPrescriptionCardHeaderTemplate();
        let signatureContTemplate = getSignatureTemplate();
        const footerTemplate = getFooterTemplate();

        /* HEADER + DOCTOR DETAILS */
        prescriptionTemp += headerTemplate

        let prescriptionBodyTemp = getDivTemplate("patient-prescription-body");
        let prescriptionBodyCardContTemp = getDivTemplate(`prescription-card`);
        let prescriptionCardContentTemplate = getDivTemplate("prescription-card-content-container").replace("$injectDivData", "$injectPrescriptionBody");

        // let note = ""
        // if (AppUtils.isTeleConsultationAppointment(props.appointmentSchedule)) {
        //     note = getDivTemplate("note").replace("$injectDivData", "Note: This prescription is generated on a teleconsultation")
        // }

        /* PRESCRIPTION BODY(PATIENT'S DETAILS + SECTIONS BODY + SIGNATURE CONTAINER) + FOOTER */
        prescriptionTemp += prescriptionBodyTemp.replace(
            "$injectDivData",
            prescriptionBodyCardContTemp.replace(
                "$injectDivData", prescriptionCardHeaderTemplate
                + "<hr/>"
            + prescriptionCardContentTemplate
            )
            + signatureContTemplate
        )
            + footerTemplate;

        return getDivTemplate("patient-prescription-template")
            .replace("$injectDivData", prescriptionTemp);
    };


    const getAllergiesTemplate = () => {
        if (prescription && !_isEmpty(prescription.allergies)) {
            // let allergiesTemplate: string = pdfTemplates.templateNameToDataMap["allergies"];
            let allergiesTemplate = allergiesContTemplate
            let allergiesBody = !_isEmpty(prescription.allergies)
                ? prescription.allergies.map((allergyItem: any) =>
                    `${allergyItem.allergen && allergyItem.allergen.displayValue}${allergyItem.isSuspected
                        ? " (suspected)"
                        : ""
                    }`
                ).join(", ") : "";
            return allergiesTemplate.replace("$injectAllergies", allergiesBody);
        }
        return "";
    }

    const getChiefSymptomsTemplate = () => {
        if (prescription && !_isEmpty(prescription.chiefComplaints)) {
            let chiefSymptomsContTemp = "";
            const chiefSymptoms = prescription.chiefComplaints
                && !_isEmpty(prescription.chiefComplaints)
                && prescription.chiefComplaints.chiefSymptoms
                ? prescription.chiefComplaints.chiefSymptoms
                : [];
            // let chiefSymptomsTemplate = pdfTemplates.templateNameToDataMap["chiefSymptoms"];
            let chiefSymptomsTemplate = chiefComplaintsTemplate
            if (chiefSymptoms.length > 0) {
                const chiefSymptomsBody = chiefSymptoms.map((symptom: ISymptom) => {
                    const symptomDesc = symptom.duration
                        && symptom.duration.count
                        && symptom.duration.unit
                        ? ` - Since ${symptom.duration.count} ${symptom.duration.unit.toLowerCase()}${symptom.duration.count > 1 ? "s" : ""}`
                        : "";
                    // return pdfTemplates.templateNameToDataMap["chiefSymptomItem"]
                    return chiefComplaintsItem
                        .replace("$injectSymptomDisplayValue", symptom.complaint.displayValue)
                        .replace("$injectSymptomDescription", symptomDesc)
                        .replace("$injectSymptomComment", symptom.comment || "");
                });
                chiefSymptomsContTemp += chiefSymptomsBody.join("\n");
            }
            if (prescription.chiefComplaints.presentIllness && prescription.chiefComplaints.presentIllness.noPresentingIllness) {
                chiefSymptomsContTemp += getDivTemplate("prescription-subheading").replace("$injectDivData", "No Presenting Illness");
            }
            // if (prescription.chiefComplaints.publicNotes) {
            //     chiefSymptomsContTemp += AppUtils.formatStrongTag(getDivTemplate().replace("$injectDivData", prescription.chiefComplaints.publicNotes));
            // }
            const finalTemplate = chiefSymptomsTemplate.replace("$injectChiefSymptomsBody", chiefSymptomsContTemp);
            return finalTemplate;
        }
        return "";
    }

    const getPublicNotesTemplate = () => {
        if (prescription && !_isEmpty(prescription.publicNotes)) {
            let notesBody: string = ""
            _map(prescription.publicNotes, (note: string) => {
                notesBody += AppUtils.formatStrongTag(note)

            })
            // const publicNotesTemplate = pdfTemplates.templateNameToDataMap["publicNotes"];
            return publicNotesTemplate.replace("$injectPublicNotesBody", notesBody);
        }
        return ""
    }

    const getDiagnosisTemplate = () => {
        if (prescription && prescription.prescriptionDiagnosis && prescription.prescriptionDiagnosis.length > 0) {
            // const diagnosisTemplate = pdfTemplates.templateNameToDataMap["diagnosis"];
            const diagnosisTemplate = prescriptionDiagnosisTemplate
            const diagnosisBody = prescription.prescriptionDiagnosis.map((diagnosis: IDiagnosis) => {
                // const diagnosisItemTemplate = pdfTemplates.templateNameToDataMap["diagnosisItem"];
                const diagnosisItemTemplate = prescriptionDiagnosisItem
                const diagnosisType = diagnosis.provisional ? `&nbsp;&#40;Provisional&#41;` : "";
                const diagnosisSpecifics = !_isEmpty(diagnosis.diagnosticDetails) ? diagnosis.diagnosticDetails : diagnosis.disease.displayValue;
                return diagnosisItemTemplate
                    .replace("$injectDiagnosis", diagnosisSpecifics)
                    .replace("$injectDiagnosisType", diagnosisType)
                    .replace("$injectDiagnosisComment", diagnosis.comment || "")
            })
            return diagnosisTemplate.replace("$injectDiagnosisBody", diagnosisBody.join("\n"));
        }
        return "";
    }

    const getGeneralExaminationsTemplate = () => {
        if (prescription && prescription.generalExaminations
            && prescription.generalExaminations.generalToggleList
            && prescription.generalExaminations.generalToggleList.examinationsList
        ) {
            // const generalExamContTemplate = pdfTemplates.templateNameToDataMap["generalExaminations"];
            // const generalExamContTemplate = generalExaminationContTemplate
            // let generalExamBody = "";
            // let generalExam = pdfTemplates.templateNameToDataMap["generalExamBody"];
            const examData = prescription.generalExaminations.generalToggleList.examinationsList
                .join(", ");
            return generalExaminationContTemplate
                .replace("$injectGeneralExamBody", examData)
                .replace("$injectComment", prescription.generalExaminations.generalToggleList.comments || "")
            // generalExamBody += generalExam;
            // if (prescription.generalExaminations.generalToggleList.comments) {
            //     generalExamBody += pdfTemplates.templateNameToDataMap["generalExamComment"].replace(
            //         "$injectGeneralExamComments",
            //         prescription.generalExaminations.generalToggleList.comments
            //     );
            // }
            // return generalExamContTemplate.replace("$injectGeneralExamBody", generalExamBody);
            // return generalExamContTemplate
        }
        return "";
    }

    const getSystemicExaminationsTemplate = () => {
        if (prescription && prescription.systemicExaminations) {
            // const systemicExamContTemplate = pdfTemplates.templateNameToDataMap["systemicExaminations"];
            const systemicExamContTemplate = systemicExaminationsContTemplate
            let systemicExamBody = "";
            if (prescription.systemicExaminations.normal && prescription.systemicExaminations.normal.length > 0) {
                // let systemicNormalExamTemplate = pdfTemplates.templateNameToDataMap["systemicNormalExam"];
                let systemicNormalExamTemplate = systemicExamItemTemplate
                const normalItems = prescription.systemicExaminations.normal.map((exm: IPatientSystemiscExamination) => {
                    const code = props.staticSystemicExams[exm.systemicExaminationCode.toUpperCase().replace(" ", "_")];
                    if (exm.systemicExaminationCode !== "Others") {
                        return code.displayValue
                    }
                }).join(", ");
                systemicNormalExamTemplate = systemicNormalExamTemplate
                    .replace("$injectSystemicExamCode", "Normal")
                    .replace("$injectSystemicExamComment", normalItems);
                systemicExamBody += systemicNormalExamTemplate;
            }
            if (prescription.systemicExaminations
                && prescription.systemicExaminations.sysExm
                && prescription.systemicExaminations.sysExm.length > 0) {
                const systemicExamItems = prescription.systemicExaminations.sysExm.map((exm: IPatientSystemiscExamination) => {
                    if (exm.comment) {
                        const code = props.staticSystemicExams[exm.systemicExaminationCode.toUpperCase().replace(" ", "_")];
                        // return pdfTemplates.templateNameToDataMap["systemicExams"]
                        return systemicExamItemTemplate
                            .replace("$injectSystemicExamCode", code ? code.displayValue : exm.systemicExaminationCode)
                            .replace("$injectSystemicExamComment", exm.comment)
                    }
                }).join("\n");
                systemicExamBody += systemicExamItems;
            }
            return systemicExamBody.length ? systemicExamContTemplate.replace("$injectSystemicExaminationBody", systemicExamBody) : "";
        }
        return "";
    };

    const getEntExaminationsTemplate = () => {
        if (prescription && !_isEmpty(prescription.entExamination)) {
            // const entExamContTemplate = pdfTemplates.templateNameToDataMap["entExaminations"];
            const entExamContTemplate = entExaminationsContTemplate
            let entExamBody = "";
            if (prescription.entExamination.normal && prescription.entExamination.normal.length > 0) {
                // let entNormalExamTemplate = pdfTemplates.templateNameToDataMap["entNormalExam"];
                let entNormalExamTemplate = entExamItemTemplate
                const normalItems = prescription.entExamination.normal.map((exm: IPatientSystemiscExamination) => {
                    const code = props.staticEntExams[exm.entExaminationCode && exm.entExaminationCode.toUpperCase().replace(" ", "_")];
                    if (exm.entExaminationCode !== "Others") {
                        return exm.entExaminationCode
                    }
                }).join(", ");
                entNormalExamTemplate = entNormalExamTemplate
                    .replace("$injectEntExamCode", "Normal")
                    .replace("$injectEntExamComment", normalItems)
                entExamBody += entNormalExamTemplate;
            }
            if (prescription.entExamination.entExm && prescription.entExamination.entExm.length > 0) {
                const entExamItems = prescription.entExamination.entExm.map((exm: IPatientSystemiscExamination) => {
                    if (exm.comment) {
                        const code = props.staticEntExams[exm.entExaminationCode.toUpperCase().replace(" ", "_")];
                        // return pdfTemplates.templateNameToDataMap["entExams"]
                        return entExamItemTemplate
                            .replace(
                                "$injectEntExamCode",
                                exm.entExaminationTagsSet
                                    ? exm.entExaminationTagsSet
                                    : exm.entExaminationCode && exm.entExaminationCode
                            )
                            .replace("$injectEntExamComment", exm.comment);
                    }
                }).join("\n");
                entExamBody += entExamItems;
            }
            return entExamContTemplate.replace("$injectEntExaminationBody", entExamBody);
        }
        return "";
    };

    const getLabTestsTemplate = () => {
        if (prescription && prescription.prescriptionLabTest && prescription.prescriptionLabTest.length > 0) {
            // const labTestContTemplate = pdfTemplates.templateNameToDataMap["labTest"];
            const labTestContTemplate = prescriptionLabTestsTemplate
            const labTestBodyItems = prescription.prescriptionLabTest.map((test: TreatmentPlanLabTestObj) => {
                const labTestListItem = prescriptionLabTestItem
                let dateText = ""
                    , instructions = ""
                    , comments = ""
                // const labTestBodyTemplate = getDivTemplate("left-details-left");
                // let labTestBody = "";
                // let testContainer = getDivTemplate("test-details-container");
                // let testDetailsLeft = getDivTemplate("test-left");
                // let testDetailsRight = getDivTemplate("test-right");
                // let numberOfTests: number;
                // if (test.diagnosis && (test.diagnosis as any).additionalInfo && (test.diagnosis as any).additionalInfo.subProducts
                //     && (test.diagnosis as any).additionalInfo.subProducts.length) {
                //     let subProductNames = (test.diagnosis as any).additionalInfo.subProducts.map((subproduct: any) => subproduct.name);
                //     numberOfTests = subProductNames.length;
                //     testDetailsRight = testDetailsRight.replace("$injectDivData", subProductNames.join(", "));
                // } else {
                //     testDetailsRight = testDetailsRight.replace("$injectDivData", "");
                // }
                // labTestBody += getSpanTemplate("prescription-subheading").replace(
                //     "$injectSpanData",
                //     (test.diagnosis && !_isEmpty(test.diagnosis.displayValue)) ?
                //         numberOfTests ? `${test.diagnosis.displayValue} (${numberOfTests})` : test.diagnosis.displayValue
                //         : ""
                // );
                if (test.labTestFrequency && (test.labTestFrequency.count || test.labTestFrequency.unit)) {
                    dateText = (test.labTestFrequency || {}).unit === "STAT"
                        ? `Immediate`
                        : `${
                        test.labTestFrequency.count
                        } ${
                        test.labTestFrequency.unit.toLowerCase()
                        }${
                        test.labTestFrequency.count > 1 ? "s" : ""
                        }`
                }
                if (test.instructions) {
                    // labTestBody += getSpanTemplate("prescription-subdesc").replace(
                    //     "$injectSpanData",
                    //     `&nbsp;-&nbsp;${test.instructions}`
                    // );
                    instructions = AppUtils.toCapitalize(test.instructions)
                }
                if (test.comments) {
                    // labTestBody += getSpanTemplate("prescription-subdesc").replace(
                    //     "$injectSpanData",
                    //     `&nbsp;&#10222;&nbsp;${test.comments}&nbsp;&#10223;`
                    // );
                    comments = test.comments
                }


                // testDetailsLeft = testDetailsLeft.replace("$injectDivData", labTestBody);
                // testContainer = testContainer.replace("$injectDivData", (testDetailsLeft + testDetailsRight));
                // return testContainer;
                // return labTestBodyTemplate.replace("$injectDivData", labTestBody);
                return labTestListItem
                    .replace("$injectLabTest", test.diagnosis.displayValue)
                    .replace("$injectDuration", dateText)
                    .replace("$injectInstructions", instructions)
                    .replace("$injectDiagnosisComment", comments)

            });
            return labTestContTemplate.replace("$injectLabTestBodyCont", labTestBodyItems.join("\n"));
        }
        return "";
    }

    const getMedicinesTemplate = () => {
        if (prescription && prescription.prescriptionMedicines && prescription.prescriptionMedicines.length > 0) {
            // const medicineContTemplate: string = pdfTemplates.templateNameToDataMap["medicines"];
            const medicineContTemplate = prescriptionMedicinesTemplate
            const medicineBodyDataItems: Array<string> = prescription.prescriptionMedicines.map((medicine: TreatmentPlanMedicinesIndexObj, index: number) => {
                // let medicineBodyData: string = "";
                // let medicineRowTemplate = getDivTemplate("medicine-row");
                const medicineItem = prescriptionMedicineItem
                const medicineName = _get(medicine, "brand.displayValue", "").toUpperCase()
                const medicineGenericName = _get(medicine, "genericName.displayValue", "").toUpperCase()
                const medicineNameString = medicineName + "<br/>" + getSpanTemplate("medicine-salt").replace("$injectSpanData", medicineGenericName)
                // const medicineColTemplate = pdfTemplates.templateNameToDataMap["medicineColumn"]
                //     .replace("$injectMedicineBrand", (medicine.brand && medicine.brand.displayValue || "").toUpperCase())
                //     .replace(
                //         "$injectMedicineNameAndDosage",
                //         `${(medicine && medicine.genericName && medicine.genericName.displayValue || "").toUpperCase().replace(
                //             "+",
                //             ", "
                //         )} ${medicine.dosage ? "| " + medicine.dosage : ""}`
                //     );

                const medicineQuantity = _get(medicine, "medicineQuantity.count", "") + " " + _get(medicine, "medicineQuantity.unit", "")
                // let medicineQuantityColTemplate = getDivTemplate("med-col-2").replace(
                //     "$injectDivData",
                //     !_isEmpty(medicine.medicineQuantity)
                //         && !_isEmpty(medicine.medicineQuantity.count)
                //         && !_isEmpty(medicine.medicineQuantity.unit)
                //         ? `${(medicine.medicineQuantity.count || "")} ${AppUtils.internalToDisplayValue(medicine.medicineQuantity.unit || "")}`
                //         : ""
                // );

                const medicineDosageString = !_isEmpty(medicine.medicineDosage) ? AppUtils.formatSlots(medicine.medicineDosage) : ""

                const medicineInstructionsString = !_isEmpty(medicine.medicineInstructions) ? AppConstants.MedicineInstructionCodes[(medicine.medicineInstructions as any)] : ""

                const medicineFrequency = medicine.frequency || ""

                let medicineDuration = ""
                if (!_isEmpty(_get(medicine, "medicineDuration.unit", null))) {
                    if (medicine.medicineDuration.unit === "SOS") medicineDuration = "As & when required"
                    else {
                        medicineDuration = medicine.medicineDuration.count + " " + AppUtils.internalToDisplayValue(medicine.medicineDuration.unit)
                    }
                }

                const medicineComment = medicine.comments || ""

                // let medicineDosageColTemplate = "";
                // if (showDosageRow(medicine)) {
                //     let rowData: string = "";
                //     if (!_isEmpty(medicine.frequency)) {
                //         rowData += getDivTemplate("dosage-col col-strt prescription-subheading").replace("$injectDivData", `${medicine.frequency}`);
                //     }
                //     if (!_isEmpty(medicine.frequency) && !_isEmpty(medicine.medicineDuration) && medicine.medicineDuration.count && medicine.medicineDuration.unit) {
                //         rowData += getDivTemplate("col-center dosage-divider").replace("$injectDivData", "for");
                //     }
                //     if (!_isEmpty(medicine.medicineDuration) && medicine.medicineDuration.unit) {
                //         let durationText = medicine.medicineDuration.unit === "SOS"
                //             ? "As & when required"
                //             : `${medicine.medicineDuration.count || ""} ${medicine.medicineDuration.unit.toLowerCase()}${medicine.medicineDuration.count > 1 ? "s" : ""}`
                //         if (medicine.medicineDuration.unit === "SOS" && durationText.length > 0) {
                //             rowData += getDivTemplate("col-center dosage-divider-2").replace("$injectDivData", "|");
                //         }
                //         rowData += getDivTemplate("dosage-col col-strt prescription-subheading").replace("$injectDivData", durationText)
                //     }
                //     medicineDosageColTemplate = getDivTemplate("med-dosage-col").replace("$injectDivData", getDivTemplate("dosage-row").replace("$injectDivData", rowData));
                // }
                // medicineRowTemplate = medicineRowTemplate.replace("$injectDivData", medicineColTemplate + medicineQuantityColTemplate + medicineDosageColTemplate);
                //
                // medicineBodyData += medicineRowTemplate;
                //
                // if (checkInstructionsExist(medicine)) {
                //     let medicineInstructionContTemplate = pdfTemplates.templateNameToDataMap["medicineInstruction"];
                //     let medicineInstructionBody = "";
                //     if (!_isEmpty(medicine.medicineDosage)) {
                //         const medicineSlotList = AppUtils.formatSlots(medicine.medicineDosage)
                //         medicineInstructionBody += getLiTemplate("prescription-subdesc").replace("$injectLiData", medicineSlotList);
                //     }
                //     if (!_isEmpty(medicine.medicineInstructions)) {
                //         medicineInstructionBody += getLiTemplate("prescription-subdesc").replace(
                //             "$injectLiData",
                //             AppConstants.MedicineInstructionCodes[(medicine.medicineInstructions as any)]
                //         );
                //         // TODO: medicineInstructionBody += getLiTemplate("prescription-subdesc").replace("$injectLiData", AppConstants.MedicineInstructionCodes[medicine.medicineInstructions]);
                //     }
                //     if (medicine.comments) {
                //         medicineInstructionBody += getLiTemplate("prescription-subdesc").replace("$injectLiData", medicine.comments);
                //     }
                //
                //     medicineBodyData += medicineInstructionContTemplate.replace("$injectMedicineInstructionsData", medicineInstructionBody);
                // }

                // return pdfTemplates.templateNameToDataMap["medicineItem"]
                return medicineItem
                    .replace("$injectMedicine", medicineNameString)
                    .replace("$injectQuantity", medicineQuantity)
                    .replace("$injectTiming", medicineDosageString)
                    .replace("$injectIntake", medicineInstructionsString)
                    .replace("$injectFrequency", medicineFrequency)
                    .replace("$injectDuration", medicineDuration)
                    .replace("$injectComment", medicineComment)
            });
            return medicineContTemplate.replace("$injectMedicineBody", medicineBodyDataItems.join("\n"));
        }
        return "";
    }

    const getAdviceTemplate = () => {
        if (prescription && prescription.prescriptionAdvice && prescription.prescriptionAdvice.length > 0) {
            // let adviceContTemplate = pdfTemplates.templateNameToDataMap["adviceTemplate"];
            let adviceContTemplate = adviceTemplate
            const adviceItems = prescription.prescriptionAdvice.map((advice: TreatmentPlanAdviceObj) => {
                // return pdfTemplates.templateNameToDataMap["adviceTemplateItem"]
                return getLiTemplate("prescription-subheading")
                    .replace("$injectLiData", (advice.doctorAdvice && !_isEmpty(advice.doctorAdvice.displayValue))
                        ? advice.doctorAdvice.displayValue
                        : ""
                    )
            });
            return adviceContTemplate.replace("$injectAdviceBody", adviceItems.join("\n"));
        }
        return "";
    }

    const getLifestyleAdviceTemplate = () => {
        if (prescription && prescription.prescriptionLifeStyleAdvice
            && prescription.prescriptionLifeStyleAdvice.lifestyle
            && prescription.prescriptionLifeStyleAdvice.lifestyle.length > 0) {
            // let lifestyleAdviceContTemplate = pdfTemplates.templateNameToDataMap["lifestyleAdvice"];
            let lifestyleAdviceContTemplate = adviceTemplate
            const adviceItems = prescription.prescriptionLifeStyleAdvice.lifestyle.map((advice: any) => {
                // return pdfTemplates.templateNameToDataMap["lifestyleAdviceItem"]
                return getLiTemplate()
                    .replace("$injectLiData", advice.lifestyleAdvice.displayValue)
                // .replace("$injectLifestyleAdviceInstruction", advice.comment
                //     ? getSpanTemplate("prescription-description").replace("$injectSpanData", `-&nbsp;${advice.comment}`)
                //     : ""
                // );
            });
            return lifestyleAdviceContTemplate.replace("$injectLifestyleAdviceBody", adviceItems.join("\n"));
        }
        return "";
    }

    const getReferralTemplate = () => {
        if (prescription && prescription.referral && prescription.referral.length > 0 && !_isEmpty(prescription.referral)) {
            // let referralContTemplate = pdfTemplates.templateNameToDataMap["referral"];
            let referralContTemplate = referralTemplate
            const referralItems = prescription.referral.map((data: TreatmentPlanReferralObj) => {
                let referralItemBody = referralItem;
                let specialist = null;
                if (data && data.doctorSpecialityCode) {
                    specialist = AppUtils.getSpecialityNameFromCode(data.doctorSpecialityCode.internalValue
                        ? data.doctorSpecialityCode.internalValue
                        : data.doctorSpecialityCode, props.staticSpecialistData
                    );
                    // referralItemBody += pdfTemplates.templateNameToDataMap["referralItemCode"].replace(
                    //     "$injectReferralCode",
                    //
                    // );
                }
                // return getDivTemplate("referral-row-item").replace("$injectDivData", referralItemBody);
                return referralItemBody
                    .replace("$injectSpeciality", specialist || data.doctorSpecialityCode)
                    .replace("$injectReferralComment", data.comments || "")
            })
            return referralContTemplate.replace("$injectReferralBody", referralItems.join("\n"));
        }
        return "";
    }

    const getFollowupTemplate = () => {
        if (prescription && !_isEmpty(prescription.followup)) {
            // let referralContTemplate = pdfTemplates.templateNameToDataMap["followUp"];
            const followupTemp = followupTemplate
            const referralItems =
                prescription.followup.map((item: TreatmentPlanFollowUpObj) => {
                    let followupItemBody = followupItem;
                    let dateText = ""
                    if (item.followUp && item.followUp.unit && item.followUp.count) {
                        dateText = `${item.followUp.count} ${AppUtils.toCapitalize(item.followUp.unit)}${item.followUp && item.followUp.count > 0 ? "s" : ""}`
                    }
                    return followupItemBody
                        .replace("$injectDate", dateText)
                        .replace("$injectFollowupComment", item.comment || "")
                })
            if (!_isEmpty(referralItems) && !_isEmpty(referralItems[0])) {
                return followupTemp.replace("$injectFollowupBody", referralItems.join("\n"));
            } else return "";
        }
        return "";
    }

    const getBodyPartsTemplate = () => {
        if (prescription && !_isEmpty(prescription.prescriptionBodyPartsCateredTo)) {
            // const bodyPartsContTemplat = pdfTemplates.templateNameToDataMap["bodyPartsCateredTo"];
            const bodyPartsContTemplat = bodyPartsContTemplate
            const bodyPartItems =
                prescription.prescriptionBodyPartsCateredTo.map((item: any) => {
                    // let bodyPartItemBody = pdfTemplates.templateNameToDataMap["bodyPartsCateredToItem"];
                    let bodyPartItemBody = bodyPartsItemTemplate;
                    return bodyPartItemBody
                        .replace("$injectBodyPartDisplayValue", item.displayValue)
                        .replace("$injectBodyPartComment", item.comments || "")
                })
            return bodyPartsContTemplat.replace("$injectBodyPartsBody", bodyPartItems.join("\n"));
        }
        return "";
    };

    let currTemplate: string = getPrescriptionPdfTemplate(pageIndex, (templateList || []).length);
    let injectedBody: string = "";
    const sectionTemplates = pdfTemplates.data.split(",").map((sectionId: string) => {
        const section = pdfTemplates.templateIdToNameMap[sectionId];
        switch (section) {
            case PrescriptionPdfSection.ALLERGIES:
                return getAllergiesTemplate();
            case PrescriptionPdfSection.CHIEF_SYMPTOMS:
                return getChiefSymptomsTemplate();
            case PrescriptionPdfSection.PUBLIC_NOTES:
                return getPublicNotesTemplate();
            case PrescriptionPdfSection.DIAGNOSIS:
                return getDiagnosisTemplate();
            case PrescriptionPdfSection.FOLLOWUP:
                return getFollowupTemplate();
            case PrescriptionPdfSection.GENERAL_ADVICE:
                return getAdviceTemplate();
            case PrescriptionPdfSection.GENERAL_EXAMINATIONS:
                return getGeneralExaminationsTemplate();
            case PrescriptionPdfSection.ENT_EXAMINATIONS:
                return getEntExaminationsTemplate();
            case PrescriptionPdfSection.LAB_TESTS:
                return getLabTestsTemplate();
            case PrescriptionPdfSection.LIFESTYLE_ADVICE:
                return getLifestyleAdviceTemplate();
            case PrescriptionPdfSection.MEDICINES:
                return getMedicinesTemplate();
            case PrescriptionPdfSection.REFERRAL:
                return getReferralTemplate();
            case PrescriptionPdfSection.SYSTEMIC_EXAMINATIONS:
                return getSystemicExaminationsTemplate();
            case PrescriptionPdfSection.BODY_PARTS:
                return getBodyPartsTemplate();
            default:
                return ""
        }
    });
    injectedBody += sectionTemplates.join("\n");
    return currTemplate.replace("$injectPrescriptionBody", injectedBody);
}